import { ComponentPropsWithoutRef } from 'react';

import styled from 'styled-components';

import { Tooltip, TooltipProps } from '@components/tooltip';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type BadgeCounterSize = 'xs' | 'sm' | 'md' | 'lg';

export type BadgeCounterVariant =
  | 'neutral'
  | 'blue'
  | 'blueTrans'
  | 'green'
  | 'greenTrans'
  | 'orange'
  | 'orangeTrans'
  | 'red'
  | 'redTrans';

export type BadgeCounterProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
  value: number;
  variant?: BadgeCounterVariant;
  size?: BadgeCounterSize;
  max?: number;
  tooltip?: string | Omit<TooltipProps, 'children'>;
};

export const BadgeCounter = ({
  value,
  variant = 'neutral',
  size = 'md',
  max,
  tooltip,
  ...rest
}: BadgeCounterProps) => {
  const colors = VARIANT_COLORS[variant];

  const content = (
    <Container $size={size} $bg={colors.bg} $color={colors.color} {...rest}>
      {max !== undefined && value > max ? `${max}+` : value}
    </Container>
  );

  if (!tooltip) return content;

  if (typeof tooltip === 'string') {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return <Tooltip {...tooltip}>{content}</Tooltip>;
};

type ResolvedColors = {
  bg: Color;
  color: Color;
};

const VARIANT_COLORS: Record<BadgeCounterVariant, ResolvedColors> = {
  neutral: { bg: 'uiTrans5', color: 'textSecondary' },
  blue: { bg: 'elementBlue100', color: 'white100' },
  blueTrans: { bg: 'blueTrans20', color: 'textSecondary' },
  green: { bg: 'elementGreen100', color: 'white100' },
  greenTrans: { bg: 'greenTrans20', color: 'textSecondary' },
  orange: { bg: 'elementOrange100', color: 'white100' },
  orangeTrans: { bg: 'orangeTrans20', color: 'textSecondary' },
  red: { bg: 'elementRed100', color: 'white100' },
  redTrans: { bg: 'redTrans20', color: 'textSecondary' },
};

const SIZE_MAP: Record<BadgeCounterSize, string> = {
  xs: pxToRem(16),
  sm: pxToRem(24),
  md: pxToRem(32),
  lg: pxToRem(40),
};

const FONT_MAP: Record<BadgeCounterSize, string> = {
  xs: theme.font.semibold10,
  sm: theme.font.bold12,
  md: theme.font.bold14,
  lg: theme.font.bold14,
};

const Container = styled.div<{
  $size: BadgeCounterSize;
  $bg: Color;
  $color: Color;
}>((p) => ({
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: SIZE_MAP[p.$size],
  height: SIZE_MAP[p.$size],
  padding: `0 ${pxToRem(4)}`,
  color: theme.color[p.$color],
  background: theme.color[p.$bg],
  borderRadius: pxToRem(4),
  font: FONT_MAP[p.$size],
}));
